import { useState, useEffect, useCallback } from 'react'
import { X, Sparkles, Image as ImageIcon, Type as TypeIcon, Loader2, Plus, Trash2, Save } from 'lucide-react'
import { generateApi, Asset, GenerationModel } from '@/services/api'
import clsx from 'clsx'

interface GenerateAssetModalProps {
  onClose: () => void
  onSuccess: () => void
  initialReferenceAsset?: Asset | null
}

type GenerationMode = 'text-to-image' | 'image-to-image'

const API_BASE = import.meta.env.VITE_API_URL ||
  (window.location.hostname === 'localhost' ? '' : 'https://aistudio-api.wizice100.workers.dev')

interface ImageReference {
  asset: Asset
  label: string
  description: string
}

export function GenerateAssetModal({ onClose, onSuccess, initialReferenceAsset }: GenerateAssetModalProps) {
  const [mode, setMode] = useState<GenerationMode>(initialReferenceAsset ? 'image-to-image' : 'text-to-image')
  const [models, setModels] = useState<GenerationModel[]>([])
  const [selectedModel, setSelectedModel] = useState('')
  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)

  // Image-to-Image specific
  const [imageReferences, setImageReferences] = useState<ImageReference[]>([])
  const [showAssetSearch, setShowAssetSearch] = useState(false)

  // Generation status tracking
  const [generatingJobId, setGeneratingJobId] = useState<string | null>(null)
  const [generationProgress, setGenerationProgress] = useState(0)

  // Asset metadata
  const [assetName, setAssetName] = useState('')
  const [assetType, setAssetType] = useState<'background' | 'character' | 'typography' | 'title-typo' | 'subtitle-typo' | 'author' | 'publisher' | 'effect' | 'layout'>('background')
  const [assetTags, setAssetTags] = useState('')

  // Image preview modal
  const [previewAsset, setPreviewAsset] = useState<Asset | null>(null)
  const [showOriginalSize, setShowOriginalSize] = useState(false)

  // Asset 등록 확인 다이얼로그
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [generationResult, setGenerationResult] = useState<{
    jobId: string
    accessKey: string
    imageUrl: string
    prompt: string
  } | null>(null)
  const [dialogMetadata, setDialogMetadata] = useState<{
    name: string
    description: string
    tags: string[]
  }>({ name: '', description: '', tags: [] })

  // Load models
  useEffect(() => {
    loadModels()
  }, [])

  // Listen for asset selection from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Check origin for security
      if (event.origin !== window.location.origin) return

      if (event.data.type === 'ASSET_SELECTED' && event.data.asset) {
        handleAddReference(event.data.asset)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [])

  // Set initial reference asset if provided
  useEffect(() => {
    if (initialReferenceAsset) {
      setImageReferences([{
        asset: initialReferenceAsset,
        label: initialReferenceAsset.name,
        description: initialReferenceAsset.description || ''
      }])
    }
  }, [initialReferenceAsset])

  const loadModels = async () => {
    const result = await generateApi.getModels()
    if (result.success && result.data) {
      setModels(result.data)
      const recommended = result.data.find(m => m.recommended)
      if (recommended) {
        setSelectedModel(recommended.id)
      }
    }
  }

  const handleAddReference = useCallback((asset: Asset) => {
    setImageReferences(prev => {
      const label = `이미지 ${prev.length + 1}`
      return [...prev, {
        asset,
        label,
        description: ''
      }]
    })
    setShowAssetSearch(false)
  }, [])

  const handleUpdateReference = (index: number, field: 'label' | 'description', value: string) => {
    const updated = [...imageReferences]
    updated[index][field] = value
    setImageReferences(updated)
  }

  const handleRemoveReference = (index: number) => {
    setImageReferences(imageReferences.filter((_, i) => i !== index))
  }

  const buildPromptWithReferences = (): string => {
    if (mode === 'text-to-image' || imageReferences.length === 0) {
      return prompt
    }

    let fullPrompt = prompt + '\n\n'
    imageReferences.forEach((ref) => {
      fullPrompt += `${ref.label}: ${ref.description}\n`
    })

    return fullPrompt.trim()
  }

  // Extract keywords from prompt text
  const extractKeywords = (text: string): string[] => {
    // 쉼표, 세미콜론, 'and', 'with' 등으로 구분
    const keywords = text
      .toLowerCase()
      .split(/[,;]|\band\b|\bwith\b|\bin\b/)
      .map(k => k.trim())
      .filter(k => k.length > 2 && k.length < 30) // 너무 짧거나 긴 키워드 제외
      .slice(0, 10) // 최대 10개
    return keywords
  }

  const pollGenerationStatus = async (jobId: string, accessKey: string): Promise<string | null> => {
    const maxAttempts = 60 // 60초 최대 대기
    const pollInterval = 1000 // 1초마다 체크

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval))

      setGenerationProgress(Math.min((attempt / maxAttempts) * 100, 95))

      try {
        const statusResult = await generateApi.getStatus(jobId, accessKey)

        if (statusResult.success && statusResult.data) {
          const status = statusResult.data.status.toLowerCase()
          if (status === 'completed' && statusResult.data.imageUrl) {
            setGenerationProgress(100)
            return statusResult.data.imageUrl
          } else if (status === 'failed') {
            throw new Error(statusResult.data.errorMessage || 'Generation failed')
          }
        }
      } catch (err) {
        console.error('Poll error:', err)
      }
    }

    throw new Error('Generation timeout')
  }

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      alert('프롬프트를 입력해주세요')
      return
    }

    if (mode === 'image-to-image' && imageReferences.length === 0) {
      alert('참고 이미지를 선택해주세요')
      return
    }

    setIsGenerating(true)
    setGenerationProgress(0)

    try {
      const finalPrompt = buildPromptWithReferences()

      let result
      if (mode === 'text-to-image') {
        // Text-to-Image
        result = await generateApi.text2img({
          prompt: finalPrompt,
          negativePrompt: negativePrompt || undefined,
          model: selectedModel,
        })
      } else {
        // Image-to-Image
        const referenceAsset = imageReferences[0].asset
        const imageUrl = `${API_BASE}/api/v1/assets/${referenceAsset.id}/image`

        // Download image
        const response = await fetch(imageUrl, {
          credentials: 'include' // HttpOnly 쿠키 인증 포함
        })

        if (!response.ok) {
          throw new Error(`이미지 다운로드 실패: ${response.status} ${response.statusText}`)
        }

        const blob = await response.blob()
        const file = new File([blob], 'reference.jpg', { type: blob.type })

        result = await generateApi.img2img({
          image: file,
          prompt: finalPrompt,
          model: selectedModel,
        })
      }

      if (!result.success || !result.data) {
        throw new Error(result.error?.message || '생성 요청 실패')
      }

      const { jobId, accessKey } = result.data
      setGeneratingJobId(jobId)

      // Poll for completion
      const imageUrl = await pollGenerationStatus(jobId, accessKey)

      if (!imageUrl) {
        throw new Error('이미지 URL을 받지 못했습니다')
      }

      // Prepare metadata for confirmation dialog
      let name = assetName.trim() || `AI 생성 - ${new Date().toLocaleString('ko-KR')}`
      let description = `Prompt: ${finalPrompt.substring(0, 200)}`
      let tags = [assetType, 'ai-generated']

      // 사용자가 입력한 태그 추가
      if (assetTags.trim()) {
        const userTags = assetTags.split(',').map(t => t.trim()).filter(Boolean)
        tags = [...tags, ...userTags]
      } else {
        // 사용자가 태그를 입력하지 않았으면 프롬프트에서 키워드 추출
        const promptKeywords = extractKeywords(finalPrompt)
        tags = [...tags, ...promptKeywords]
      }

      // 중복 제거
      tags = Array.from(new Set(tags))

      // Set generation result and dialog metadata
      setGenerationResult({ jobId, accessKey, imageUrl, prompt: finalPrompt })
      setDialogMetadata({ name, description, tags })
      setShowSaveDialog(true)
      setIsGenerating(false)
      setGenerationProgress(0)
    } catch (err) {
      console.error('Generation error:', err)
      alert(`생성 중 오류: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    } finally {
      setIsGenerating(false)
      setGeneratingJobId(null)
      setGenerationProgress(0)
    }
  }

  // Confirm and save asset
  const handleConfirmSave = async () => {
    if (!generationResult) return

    try {
      setIsGenerating(true)

      const saveResult = await generateApi.saveAsAsset({
        jobId: generationResult.jobId,
        accessKey: generationResult.accessKey,
        name: dialogMetadata.name.trim() || undefined,
        type: assetType,
        tags: dialogMetadata.tags.join(', '),
        description: dialogMetadata.description,
      })

      if (saveResult.success) {
        alert(`이미지가 생성되어 Assets에 저장되었습니다!\n\n등록된 키워드: ${dialogMetadata.tags.join(', ')}`)
        setShowSaveDialog(false)
        setGenerationResult(null)
        onSuccess()
        onClose()
      } else {
        throw new Error(saveResult.error?.message || 'Asset 저장 실패')
      }
    } catch (err) {
      console.error('Save asset error:', err)
      alert(`저장 중 오류: ${err instanceof Error ? err.message : '알 수 없는 오류'}`)
    } finally {
      setIsGenerating(false)
    }
  }

  return (
    <>
      {/* Main Generate Modal */}
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-bg-primary rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="sticky top-0 bg-bg-primary border-b border-border-subtle p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Sparkles className="w-6 h-6 text-accent-gold" />
              <h2 className="text-xl font-bold">AI Asset 생성</h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Generation Mode */}
          <div>
            <label className="block text-sm font-medium mb-2">생성 방식</label>
            <div className="flex gap-2">
              <button
                onClick={() => setMode('text-to-image')}
                className={clsx(
                  'flex-1 py-3 px-4 rounded-lg border transition-colors',
                  mode === 'text-to-image'
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : 'border-border-default hover:bg-bg-elevated'
                )}
                disabled={isGenerating}
              >
                <TypeIcon className="w-4 h-4 inline mr-2" />
                Text-to-Image
                <p className="text-xs mt-1 opacity-70">텍스트 프롬프트로 새 이미지 생성</p>
              </button>
              <button
                onClick={() => setMode('image-to-image')}
                className={clsx(
                  'flex-1 py-3 px-4 rounded-lg border transition-colors',
                  mode === 'image-to-image'
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : 'border-border-default hover:bg-bg-elevated'
                )}
                disabled={isGenerating}
              >
                <ImageIcon className="w-4 h-4 inline mr-2" />
                Image-to-Image
                <p className="text-xs mt-1 opacity-70">기존 이미지를 참고하여 생성</p>
              </button>
            </div>
          </div>

          {/* Model Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">생성 모델</label>
            <select
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              className="input w-full"
              disabled={isGenerating}
            >
              {models.map(model => (
                <option key={model.id} value={model.id}>
                  {model.name} {model.recommended ? '(추천)' : ''} - {model.cost} 포인트
                </option>
              ))}
            </select>
          </div>

          {/* Image References (Image-to-Image only) */}
          {mode === 'image-to-image' && (
            <div>
              <label className="block text-sm font-medium mb-2">참고 이미지</label>

              {/* Reference Images List */}
              <div className="space-y-3 mb-3">
                {imageReferences.map((ref, index) => (
                  <div key={index} className="border border-border-default rounded-lg p-3">
                    <div className="flex gap-3">
                      {/* Thumbnail */}
                      <img
                        src={`${API_BASE}/api/v1/assets/${ref.asset.id}/image`}
                        alt={ref.asset.name}
                        className="w-20 h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={() => setPreviewAsset(ref.asset)}
                      />

                      {/* Info */}
                      <div className="flex-1 space-y-2">
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={ref.label}
                            onChange={(e) => handleUpdateReference(index, 'label', e.target.value)}
                            placeholder="이미지 1"
                            className="input flex-1 text-sm"
                            disabled={isGenerating}
                          />
                          <button
                            onClick={() => handleRemoveReference(index)}
                            className="p-2 hover:bg-red-500/10 text-red-500 rounded"
                            disabled={isGenerating}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <input
                          type="text"
                          value={ref.description}
                          onChange={(e) => handleUpdateReference(index, 'description', e.target.value)}
                          placeholder="이 이미지에 대한 설명 (예: 밤 하늘의 도시 배경)"
                          className="input w-full text-sm"
                          disabled={isGenerating}
                        />
                        <p className="text-xs text-text-tertiary">Asset: {ref.asset.name}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Add Reference Button */}
              <button
                onClick={() => setShowAssetSearch(true)}
                className="btn btn-secondary w-full"
                disabled={isGenerating}
              >
                <Plus className="w-4 h-4 mr-2" />
                참고 이미지 추가
              </button>

              {/* Asset Search Modal with iframe */}
              {showAssetSearch && (
                <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
                  <div className="bg-bg-primary rounded-lg shadow-xl w-[95vw] h-[90vh] overflow-hidden flex flex-col">
                    <div className="p-4 border-b border-border-subtle flex items-center justify-between">
                      <h3 className="text-lg font-bold">참고 이미지 선택</h3>
                      <button
                        onClick={() => setShowAssetSearch(false)}
                        className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    <div className="flex-1 overflow-hidden">
                      <iframe
                        src="/assets?embedMode=true&selectMode=true"
                        className="w-full h-full border-none"
                        title="Asset Selection"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Prompt */}
          <div>
            <label className="block text-sm font-medium mb-2">프롬프트 *</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={mode === 'text-to-image'
                ? "생성할 이미지에 대한 설명을 입력하세요..."
                : "참고 이미지를 기반으로 어떻게 변형할지 설명하세요..."}
              rows={6}
              className="input w-full resize-none font-mono text-sm"
              disabled={isGenerating}
            />
            {mode === 'image-to-image' && imageReferences.length > 0 && (
              <p className="text-xs text-text-tertiary mt-2">
                💡 프롬프트에 참고 이미지 설명이 자동으로 추가됩니다
              </p>
            )}
          </div>

          {/* Negative Prompt (Text-to-Image only) */}
          {mode === 'text-to-image' && (
            <div>
              <label className="block text-sm font-medium mb-2">Negative 프롬프트</label>
              <textarea
                value={negativePrompt}
                onChange={(e) => setNegativePrompt(e.target.value)}
                placeholder="생성하지 말아야 할 요소 (선택사항)"
                rows={3}
                className="input w-full resize-none font-mono text-sm"
                disabled={isGenerating}
              />
            </div>
          )}

          {/* Asset Metadata */}
          <div className="border-t border-border-subtle pt-6 space-y-4">
            <h3 className="text-sm font-semibold text-text-secondary">저장 설정</h3>

            {/* Asset Name */}
            <div>
              <label className="block text-sm font-medium mb-2">
                Asset 이름
                <span className="text-text-tertiary ml-1">(선택사항)</span>
              </label>
              <input
                type="text"
                value={assetName}
                onChange={(e) => setAssetName(e.target.value)}
                placeholder="비워두면 자동 생성 (예: AI 생성 - 2024-01-01 12:00)"
                className="input w-full"
                disabled={isGenerating}
              />
            </div>

            {/* Asset Type */}
            <div>
              <label className="block text-sm font-medium mb-2">분류</label>
              <select
                value={assetType}
                onChange={(e) => setAssetType(e.target.value as any)}
                className="input w-full"
                disabled={isGenerating}
              >
                <option value="background">배경 (Background)</option>
                <option value="character">캐릭터 (Character)</option>
                <option value="typography">타이포그래피 (Typography)</option>
                <option value="title-typo">제목 타이포 (Title Typography)</option>
                <option value="subtitle-typo">부제목 타이포 (Subtitle Typography)</option>
                <option value="author">저자 (Author)</option>
                <option value="publisher">출판사 (Publisher)</option>
                <option value="effect">효과 (Effect)</option>
                <option value="layout">통합 (Layout)</option>
              </select>
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-2">
                태그
                <span className="text-text-tertiary ml-1">(쉼표로 구분)</span>
              </label>
              <input
                type="text"
                value={assetTags}
                onChange={(e) => setAssetTags(e.target.value)}
                placeholder="예: 판타지, 성, 밤"
                className="input w-full"
                disabled={isGenerating}
              />
              <p className="text-xs text-text-tertiary mt-1">
                💡 자동으로 'ai-generated' 태그가 추가됩니다
              </p>
            </div>
          </div>

          {/* Preview (Image-to-Image) */}
          {mode === 'image-to-image' && imageReferences.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-2">최종 프롬프트 미리보기</label>
              <div className="bg-bg-elevated border border-border-default rounded-lg p-3">
                <pre className="text-xs whitespace-pre-wrap font-mono">
                  {buildPromptWithReferences()}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-primary border-t border-border-subtle p-4">
          {/* Progress bar */}
          {isGenerating && generationProgress > 0 && (
            <div className="mb-3">
              <div className="flex items-center justify-between text-sm mb-1">
                <span className="text-text-secondary">생성 중...</span>
                <span className="text-accent-gold">{Math.round(generationProgress)}%</span>
              </div>
              <div className="w-full bg-bg-elevated rounded-full h-2 overflow-hidden">
                <div
                  className="bg-accent-gold h-full transition-all duration-300"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
              {generatingJobId && (
                <p className="text-xs text-text-tertiary mt-1">Job ID: {generatingJobId}</p>
              )}
            </div>
          )}

          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="btn btn-secondary flex-1"
              disabled={isGenerating}
            >
              취소
            </button>
            <button
              onClick={handleGenerate}
              className="btn btn-primary flex-1"
              disabled={isGenerating}
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  생성 중...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  생성하기
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>

      {/* Image Preview Modal */}
      {previewAsset && (
        <div
          className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4"
          onClick={() => {
            setPreviewAsset(null)
            setShowOriginalSize(false)
          }}
        >
          <div className="relative max-w-7xl max-h-[95vh] w-full h-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between mb-4 text-white">
              <div>
                <h3 className="text-lg font-bold">{previewAsset.name}</h3>
                <p className="text-sm text-gray-400">클릭하면 닫힙니다</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Original Size Toggle */}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowOriginalSize(!showOriginalSize)
                  }}
                  className={clsx(
                    'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                    showOriginalSize
                      ? 'bg-accent-gold text-black'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  )}
                >
                  {showOriginalSize ? '화면에 맞춤' : '원본 크기'}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setPreviewAsset(null)
                    setShowOriginalSize(false)
                  }}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Image Container */}
            <div
              className={clsx(
                "flex-1 rounded-lg",
                showOriginalSize
                  ? "overflow-auto"
                  : "flex items-center justify-center overflow-hidden"
              )}
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={`${API_BASE}/api/v1/assets/${previewAsset.id}/image`}
                alt={previewAsset.name}
                className={clsx(
                  "rounded-lg",
                  showOriginalSize
                    ? "cursor-move"
                    : "max-w-full max-h-full object-contain cursor-default"
                )}
              />
            </div>
          </div>
        </div>
      )}

      {/* Asset 등록 확인 다이얼로그 */}
      {showSaveDialog && generationResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-bg-primary rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Asset 등록 확인</h2>
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
                  disabled={isGenerating}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                {/* 미리보기 이미지 */}
                <div>
                  <label className="block text-sm font-medium mb-2">미리보기</label>
                  <div className="aspect-video bg-bg-elevated rounded-lg overflow-hidden flex items-center justify-center">
                    <img
                      src={generationResult.imageUrl}
                      alt="Preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                </div>

                {/* 제목 */}
                <div>
                  <label className="block text-sm font-medium mb-2">제목 *</label>
                  <input
                    type="text"
                    value={dialogMetadata.name}
                    onChange={(e) => setDialogMetadata({
                      ...dialogMetadata,
                      name: e.target.value
                    })}
                    className="input w-full"
                    placeholder="Asset 제목을 입력하세요"
                    disabled={isGenerating}
                  />
                </div>

                {/* 분류 (읽기 전용 표시) */}
                <div>
                  <label className="block text-sm font-medium mb-2">분류</label>
                  <div className="p-3 bg-bg-elevated rounded-lg text-sm">
                    {assetType === 'background' && '배경 (Background)'}
                    {assetType === 'character' && '캐릭터 (Character)'}
                    {assetType === 'typography' && '타이포그래피 (Typography)'}
                    {assetType === 'title-typo' && '제목 타이포 (Title Typography)'}
                    {assetType === 'subtitle-typo' && '부제목 타이포 (Subtitle Typography)'}
                    {assetType === 'author' && '저자 (Author)'}
                    {assetType === 'publisher' && '출판사 (Publisher)'}
                    {assetType === 'effect' && '효과 (Effect)'}
                    {assetType === 'layout' && '통합 (Layout)'}
                  </div>
                </div>

                {/* 설명 */}
                <div>
                  <label className="block text-sm font-medium mb-2">설명</label>
                  <textarea
                    value={dialogMetadata.description}
                    onChange={(e) => setDialogMetadata({
                      ...dialogMetadata,
                      description: e.target.value
                    })}
                    className="input w-full min-h-[100px]"
                    placeholder="Asset 설명을 입력하세요"
                    disabled={isGenerating}
                  />
                </div>

                {/* 태그 */}
                <div>
                  <label className="block text-sm font-medium mb-2">태그 (자동 추출)</label>
                  <div className="flex flex-wrap gap-2 p-3 bg-bg-elevated rounded-lg min-h-[60px]">
                    {dialogMetadata.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-accent-gold/10 text-accent-gold rounded-full text-sm"
                      >
                        {tag}
                        <button
                          onClick={() => {
                            const newTags = dialogMetadata.tags.filter((_, i) => i !== index)
                            setDialogMetadata({
                              ...dialogMetadata,
                              tags: newTags
                            })
                          }}
                          className="hover:bg-accent-gold/20 rounded-full p-0.5"
                          disabled={isGenerating}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                  <p className="text-xs text-text-tertiary mt-2">
                    프롬프트에서 자동 추출된 키워드입니다. X 버튼으로 제거할 수 있습니다.
                  </p>
                </div>

                {/* 프롬프트 (읽기 전용) */}
                <div>
                  <label className="block text-sm font-medium mb-2">프롬프트 (참고용)</label>
                  <div className="p-3 bg-bg-elevated rounded-lg text-sm text-text-secondary max-h-[200px] overflow-y-auto">
                    {generationResult.prompt}
                  </div>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowSaveDialog(false)}
                  className="btn btn-secondary flex-1"
                  disabled={isGenerating}
                >
                  취소
                </button>
                <button
                  onClick={handleConfirmSave}
                  className="btn btn-primary flex-1"
                  disabled={isGenerating || !dialogMetadata.name.trim()}
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      등록 중...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Asset 등록
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
